import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";
import { 
  Scissors, 
  Layers, 
  TreeDeciduous, 
  Sprout, 
  Star,
  Phone,
  Mail,
  CheckCircle,
  ArrowRight,
  Flower2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_landscape-pros-12/artifacts/pgiiqdu2_Untitled%20design%20%281%29.png";
const HERO_BG = "https://images.unsplash.com/photo-1770169096244-90f4288392d7?auto=format&fit=crop&w=2000&q=80";

const iconMap = {
  scissors: Scissors,
  layers: Layers,
  "tree-deciduous": TreeDeciduous,
  sprout: Sprout,
  flower: Flower2,
};

// Vivid garden images
const gardenImages = {
  hero: "https://images.unsplash.com/photo-1770169096244-90f4288392d7?auto=format&fit=crop&w=2000&q=80",
  garden1: "https://images.unsplash.com/photo-1759355787109-fd0df9c47e2e?auto=format&fit=crop&w=800&q=80",
  garden2: "https://images.unsplash.com/photo-1772040942277-b194d9d0b648?auto=format&fit=crop&w=800&q=80",
  garden3: "https://images.unsplash.com/photo-1701750204317-213f30c7fd6d?auto=format&fit=crop&w=800&q=80",
  lawn: "https://images.unsplash.com/photo-1764296377273-4299e3f3d6f5?auto=format&fit=crop&w=800&q=80",
  flowers: "https://images.pexels.com/photos/47313/tulips-bloom-blossom-colorful-47313.jpeg?auto=compress&w=800",
};

export const HomePage = () => {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, testimonialsRes, plansRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/pricing-plans`)
        ]);
        setServices(servicesRes.data.slice(0, 4));
        setTestimonials(testimonialsRes.data);
        setPlans(plansRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <img 
            src={LOGO_URL} 
            alt="Elite Blade" 
            className="w-full max-w-3xl md:max-w-4xl lg:max-w-5xl mx-auto mb-8"
            data-testid="hero-logo"
          />
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading text-white mb-4 drop-shadow-lg">
            Elevating Every
            <span className="text-primary block drop-shadow-lg">Property</span>
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8 drop-shadow-md">
            Professional landscaping & grounds care services in the Greater Houston area. 
            Transform your outdoor space with our expert team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                data-testid="hero-cta-quote"
                size="lg"
                className="bg-accent hover:bg-accent/90 text-black font-accent text-lg px-8 py-6 active:scale-95 transition-transform shadow-xl"
              >
                Get a Free Quote
              </Button>
            </Link>
            <Link to="/services">
              <Button 
                data-testid="hero-cta-services"
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-primary font-accent text-lg px-8 py-6 backdrop-blur-sm"
              >
                Our Services
              </Button>
            </Link>
          </div>
          <div className="mt-12 flex items-center justify-center gap-8 text-white/90">
            <a href="tel:7134999372" className="flex items-center hover:text-primary transition-colors">
              <Phone className="h-5 w-5 mr-2" />
              (713) 499-9372
            </a>
            <a href="mailto:info@elitebladelgc.com" className="flex items-center hover:text-primary transition-colors">
              <Mail className="h-5 w-5 mr-2" />
              info@elitebladelgc.com
            </a>
          </div>
        </div>
      </section>

      {/* Garden Showcase Section */}
      <section className="py-16 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 row-span-2 rounded-2xl overflow-hidden shadow-xl border-4 border-white">
              <img 
                src={gardenImages.garden1}
                alt="Beautiful Garden"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg border-4 border-white">
              <img 
                src={gardenImages.flowers}
                alt="Colorful Flowers"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg border-4 border-white">
              <img 
                src={gardenImages.lawn}
                alt="Manicured Lawn"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg border-4 border-white">
              <img 
                src={gardenImages.garden2}
                alt="Landscaped Garden"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg border-4 border-white">
              <img 
                src={gardenImages.garden3}
                alt="Flower Garden"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section data-testid="services-preview" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary via-emerald-400 to-primary"></div>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-primary/10 text-primary font-accent text-sm rounded-full mb-4">WHAT WE OFFER</span>
            <h2 className="text-4xl md:text-5xl font-heading text-gray-900 mb-4">
              Our <span className="text-primary">Services</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From basic lawn maintenance to complete property transformations, 
              we deliver professional results every time.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Scissors;
              return (
                <Card 
                  key={service.id}
                  data-testid={`service-card-${service.id}`}
                  className="bg-gradient-to-br from-white to-emerald-50 border-2 border-emerald-100 hover:border-primary hover:shadow-xl transition-all duration-300 group cursor-pointer"
                >
                  <CardContent className="p-6">
                    <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-primary to-emerald-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-lg font-heading text-gray-900 mb-2">{service.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                    <p className="text-accent font-accent text-sm font-bold">{service.price}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                data-testid="view-all-services-btn"
                variant="outline" 
                className="border-2 border-primary text-primary hover:bg-primary hover:text-white font-accent px-8"
              >
                View All Services <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us - With Large Image */}
      <section data-testid="why-choose-us" className="py-24 bg-gradient-to-br from-emerald-900 via-emerald-800 to-green-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={gardenImages.garden2} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1 bg-white/20 text-white font-accent text-sm rounded-full mb-4">WHY ELITE BLADE</span>
              <h2 className="text-4xl md:text-5xl font-heading text-white mb-6">
                Why Choose <span className="text-accent">Elite Blade?</span>
              </h2>
              <p className="text-emerald-100 mb-8 text-lg">
                With years of experience serving the Greater Houston area, we've built 
                our reputation on quality workmanship, reliability, and customer satisfaction.
              </p>
              <ul className="space-y-4">
                {[
                  "Fully Insured & Licensed",
                  "Consistent & Reliable Service",
                  "Professional Equipment & Techniques",
                  "Flexible Scheduling Options",
                  "Competitive Pricing",
                  "100% Satisfaction Guaranteed"
                ].map((item, index) => (
                  <li key={index} className="flex items-center text-white">
                    <div className="h-6 w-6 rounded-full bg-accent flex items-center justify-center mr-3 flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-black" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/about" className="inline-block mt-8">
                <Button 
                  data-testid="learn-more-btn"
                  className="bg-accent hover:bg-accent/90 text-black font-accent active:scale-95 transition-transform px-8"
                >
                  Learn More About Us
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden border-4 border-white/20 shadow-2xl">
                <img 
                  src={gardenImages.garden1}
                  alt="Professional Lawn Care"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-accent text-black p-6 rounded-2xl shadow-xl">
                <p className="font-heading text-4xl">5+</p>
                <p className="font-accent text-sm">Years Experience</p>
              </div>
              <div className="absolute -top-6 -right-6 bg-white text-primary p-6 rounded-2xl shadow-xl">
                <p className="font-heading text-4xl">500+</p>
                <p className="font-accent text-sm">Happy Clients</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section data-testid="pricing-preview" className="py-24 bg-gradient-to-b from-white to-gray-50 relative">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-accent via-yellow-400 to-accent"></div>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-accent/10 text-accent font-accent text-sm rounded-full mb-4">PRICING PLANS</span>
            <h2 className="text-4xl md:text-5xl font-heading text-gray-900 mb-4">
              Service <span className="text-primary">Plans</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choose the plan that fits your needs. From basic maintenance to 
              comprehensive property care.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <Card 
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`bg-white border-2 relative ${
                  plan.highlight 
                    ? 'border-accent scale-105 shadow-2xl' 
                    : 'border-gray-200 hover:border-primary hover:shadow-xl'
                } transition-all duration-300 rounded-2xl overflow-hidden`}
              >
                {plan.highlight && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent via-yellow-400 to-accent"></div>
                )}
                {plan.highlight && (
                  <div className="absolute -top-0 left-1/2 -translate-x-1/2 translate-y-0">
                    <span className="bg-accent text-black text-xs font-accent px-4 py-1.5 rounded-b-full shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardContent className="p-8 text-center pt-10">
                  <h3 className="text-xl font-heading text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-5xl font-heading text-primary">{plan.price}</span>
                    <span className="text-gray-500">/{plan.period}</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-6">{plan.description}</p>
                  <ul className="space-y-3 mb-8 text-left">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-700 text-sm">
                        <CheckCircle className="h-5 w-5 text-primary mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact">
                    <Button 
                      data-testid={`plan-cta-${plan.id}`}
                      className={`w-full font-accent py-6 ${
                        plan.highlight 
                          ? 'bg-accent hover:bg-accent/90 text-black' 
                          : 'bg-primary hover:bg-primary/90 text-white'
                      }`}
                    >
                      Get Started
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/pricing">
              <Button 
                data-testid="view-pricing-btn"
                variant="outline" 
                className="border-2 border-primary text-primary hover:bg-primary hover:text-white font-accent px-8"
              >
                View Full Pricing <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src={gardenImages.lawn} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-white/10 text-white font-accent text-sm rounded-full mb-4">TESTIMONIALS</span>
            <h2 className="text-4xl md:text-5xl font-heading text-white mb-4">
              What Our <span className="text-accent">Clients Say</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Don't just take our word for it. Here's what our satisfied customers 
              have to say about Elite Blade.
            </p>
          </div>

          <Carousel 
            opts={{ align: "start", loop: true }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {testimonials.map((testimonial) => (
                <CarouselItem key={testimonial.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <Card 
                    data-testid={`testimonial-${testimonial.id}`}
                    className="bg-white/10 backdrop-blur-sm border-white/20 h-full"
                  >
                    <CardContent className="p-6">
                      <div className="flex mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 text-accent fill-accent" />
                        ))}
                      </div>
                      <p className="text-white mb-6 italic leading-relaxed">"{testimonial.text}"</p>
                      <div className="border-t border-white/20 pt-4">
                        <p className="font-semibold text-white">{testimonial.name}</p>
                        <p className="text-gray-400 text-sm">{testimonial.location}</p>
                        <p className="text-primary text-xs font-accent mt-1">{testimonial.service}</p>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-4 bg-white border-gray-300 text-gray-700 hover:bg-gray-100" />
            <CarouselNext className="hidden md:flex -right-4 bg-white border-gray-300 text-gray-700 hover:bg-gray-100" />
          </Carousel>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 bg-gradient-to-r from-primary via-emerald-500 to-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={gardenImages.flowers} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-heading text-white mb-6 drop-shadow-lg">
            Ready to Transform Your Property?
          </h2>
          <p className="text-white/90 text-lg mb-8 drop-shadow-md">
            Get a free quote today and see why homeowners across Houston trust Elite Blade 
            for their landscaping needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                data-testid="cta-quote-btn"
                size="lg"
                className="bg-accent hover:bg-accent/90 text-black font-accent text-lg px-8 shadow-xl"
              >
                Get Your Free Quote
              </Button>
            </Link>
            <a href="tel:7134999372">
              <Button 
                data-testid="cta-call-btn"
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-primary font-accent text-lg px-8"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Damion Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
